import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

export interface MockAuthResult {
  isAuthenticated: boolean;
  userData: any;
  accessToken: string;
  idToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class MockAuthService {
  private readonly _isAuthenticated = new BehaviorSubject<boolean>(false);
  private readonly _userData = new BehaviorSubject<any>(null);
  private readonly _accessToken = new BehaviorSubject<string>('');
  private readonly _idToken = new BehaviorSubject<string>('');

  // Observables für die Auth-State
  readonly isAuthenticated$ = this._isAuthenticated.asObservable();
  readonly userData$ = this._userData.asObservable();
  readonly accessToken$ = this._accessToken.asObservable();
  readonly idToken$ = this._idToken.asObservable();

  constructor() {
    // Check if user was previously logged in (localStorage persistence)
    this.checkStoredAuth();
  }

  /**
   * Mock login - simuliert erfolgreiche Authentifizierung
   */
  login(): Observable<MockAuthResult> {
    const mockUser = {
      sub: 'local-dev-user-123',
      email: 'dev@dx-tract.local',
      given_name: 'Dev',
      family_name: 'User',
      name: 'Dev User',
      preferred_username: 'devuser',
      tenant_id: 'local-tenant-001',
      roles: ['Admin', 'User'],
      'cognito:groups': 'Admin',
    };

    const mockTokens = {
      accessToken: 'mock-access-token-local-dev',
      idToken: 'mock-id-token-local-dev',
      refreshToken: 'mock-refresh-token-local-dev',
    };

    // Update state
    this._isAuthenticated.next(true);
    this._userData.next(mockUser);
    this._accessToken.next(mockTokens.accessToken);
    this._idToken.next(mockTokens.idToken);

    // Store in localStorage for persistence
    localStorage.setItem(
      'mock-auth-state',
      JSON.stringify({
        isAuthenticated: true,
        userData: mockUser,
        accessToken: mockTokens.accessToken,
        idToken: mockTokens.idToken,
      })
    );

    console.log('🎭 Mock Auth - Login successful:', mockUser);

    return of({
      isAuthenticated: true,
      userData: mockUser,
      accessToken: mockTokens.accessToken,
      idToken: mockTokens.idToken,
    });
  }

  /**
   * Mock logout
   */
  logout(): Observable<any> {
    this._isAuthenticated.next(false);
    this._userData.next(null);
    this._accessToken.next('');
    this._idToken.next('');

    // Clear localStorage
    localStorage.removeItem('mock-auth-state');

    console.log('🎭 Mock Auth - Logout successful');

    return of(true);
  }

  /**
   * Get current access token
   */
  getAccessToken(): Observable<string> {
    return this.accessToken$;
  }

  /**
   * Get current ID token
   */
  getIdToken(): Observable<string> {
    return this.idToken$;
  }

  /**
   * Check authentication status
   */
  checkAuth(): Observable<MockAuthResult> {
    return of({
      isAuthenticated: this._isAuthenticated.value,
      userData: this._userData.value,
      accessToken: this._accessToken.value,
      idToken: this._idToken.value,
    });
  }

  /**
   * Get authentication result (compatible with OidcSecurityService interface)
   */
  getAuthenticationResult(): Observable<MockAuthResult> {
    return this.checkAuth();
  }

  /**
   * Check if there's stored auth state and restore it
   */
  private checkStoredAuth(): void {
    const storedAuth = localStorage.getItem('mock-auth-state');
    if (storedAuth) {
      try {
        const authState = JSON.parse(storedAuth);
        if (authState.isAuthenticated) {
          this._isAuthenticated.next(true);
          this._userData.next(authState.userData);
          this._accessToken.next(authState.accessToken);
          this._idToken.next(authState.idToken);
          console.log('🎭 Mock Auth - Restored from storage:', authState.userData);
        }
      } catch (error) {
        console.error('🎭 Mock Auth - Error restoring auth state:', error);
        localStorage.removeItem('mock-auth-state');
      }
    }
  }

  /**
   * Auto-login for development convenience
   */
  autoLogin(): void {
    if (!this._isAuthenticated.value) {
      this.login().subscribe();
    }
  }
}
